/**
 * First-touch traffic attribution.
 *
 * On the visitor's very first page view we capture where they came from and
 * keep it in localStorage. Every public form sends it along on submit so the
 * contact record records how the lead originally found us.
 */

const KEY = "wg_attribution";

export type TrafficSource =
  | "organic_search"
  | "paid_search"
  | "social"
  | "referral"
  | "email"
  | "direct";

export type Attribution = {
  traffic_source: TrafficSource;
  referrer: string;
  landing_page: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  captured_at: string;
};

const SEARCH_HOSTS = /(google|bing|yahoo|duckduckgo|ecosia|baidu|yandex|brave)\./i;
const SOCIAL_HOSTS =
  /(facebook|instagram|linkedin|pinterest|twitter|x\.com|t\.co|tiktok|reddit|youtube)\./i;

const classify = (referrer: string, params: URLSearchParams): TrafficSource => {
  const medium = (params.get("utm_medium") || "").toLowerCase();
  const source = (params.get("utm_source") || "").toLowerCase();

  if (params.get("gclid") || params.get("gbraid") || params.get("wbraid")) return "paid_search";
  if (/cpc|ppc|paid|sem/.test(medium)) return "paid_search";
  if (/email|newsletter/.test(medium) || /email|klaviyo|mailchimp/.test(source)) return "email";
  if (/social|paid_social/.test(medium) || SOCIAL_HOSTS.test(source)) return "social";
  if (/organic/.test(medium)) return "organic_search";

  if (!referrer) return "direct";
  try {
    const host = new URL(referrer).hostname;
    if (typeof window !== "undefined" && host === window.location.hostname) return "direct";
    if (SEARCH_HOSTS.test(host)) return "organic_search";
    if (SOCIAL_HOSTS.test(host)) return "social";
    return "referral";
  } catch {
    return "direct";
  }
};

/** Capture first-touch attribution once per browser. Safe to call on every load. */
export const captureAttribution = (): Attribution | null => {
  if (typeof window === "undefined") return null;
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return JSON.parse(existing) as Attribution;

    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer || "";
    const attribution: Attribution = {
      traffic_source: classify(referrer, params),
      referrer,
      landing_page: window.location.pathname + window.location.search,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return null;
  }
};

/** Attribution payload to attach to a form submission (always an object). */
export const getAttribution = (): Record<string, string> => {
  const a = captureAttribution();
  if (!a) return {};
  return {
    traffic_source: a.traffic_source,
    referrer: a.referrer,
    landing_page: a.landing_page,
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
  };
};
